export type NdefRecordPrimitives = {
  tnf: number;
  type: Array<number>;
  id: Array<number>;
  payload: Array<number>;
};

// An NFC Forum Text record, the format NFCService decodes when it reads a tag.
export class NdefTextRecord {
  private static readonly TNF_WELL_KNOWN = 0x01;
  private static readonly TEXT_TYPE = [0x54];
  private static readonly LANGUAGE_CODE = 'en';
  private static readonly SHORT_RECORD_MAX_PAYLOAD_LENGTH = 255;

  private readonly payload: Array<number>;

  constructor(readonly text: string) {
    const encoder = new TextEncoder();
    const languageCode = Array.from(encoder.encode(NdefTextRecord.LANGUAGE_CODE));
    const textBytes = Array.from(encoder.encode(text));
    // The status byte only carries the length of the language code: its top bit left at 0 declares UTF-8.
    this.payload = [languageCode.length, ...languageCode, ...textBytes];
  }

  static isTextRecord(record: Pick<NdefRecordPrimitives, 'tnf' | 'type'>): boolean {
    const type = record.type ?? [];
    const isText =
      record.tnf === NdefTextRecord.TNF_WELL_KNOWN &&
      type.length === NdefTextRecord.TEXT_TYPE.length &&
      type.every((byte, index) => byte === NdefTextRecord.TEXT_TYPE[index]);
    return isText;
  }

  toPrimitives(): NdefRecordPrimitives {
    return {
      tnf: NdefTextRecord.TNF_WELL_KNOWN,
      type: [...NdefTextRecord.TEXT_TYPE],
      id: [],
      payload: [...this.payload],
    };
  }

  // Bytes the record takes on a tag as the only record of its message: a header byte, the type length, the payload
  // length (one byte for a short record, four otherwise), the type and the payload.
  sizeInBytes(): number {
    const payloadLengthBytes = this.payload.length <= NdefTextRecord.SHORT_RECORD_MAX_PAYLOAD_LENGTH ? 1 : 4;
    const size = 1 + 1 + payloadLengthBytes + NdefTextRecord.TEXT_TYPE.length + this.payload.length;
    return size;
  }
}
